import type { ReactNode } from "react";
import { formatIncludedList } from "./included-list";
import type { ListingData } from "./listing-section-data";
import { TINTED_PANEL_CLASS } from "@/components/shared/tintedPanel";

export function hasPriceContent(data: {
    priceLabel?: string;
    avgLabel?: string;
    avgPrice?: string;
}): boolean {
    return Boolean(data.priceLabel || (data.avgLabel && data.avgPrice));
}

function PricePanel({ children }: { children: ReactNode }) {
    return <div className={TINTED_PANEL_CLASS}>{children}</div>;
}

function benchmarkBreedOf(benchmarkLabel: string) {
    return benchmarkLabel.replace(/^Typical /i, "");
}

function BenchmarkOnlyPrice({
    benchmarkPrice,
    benchmarkLabel,
}: {
    benchmarkPrice: string;
    benchmarkLabel: string;
}) {
    return (
        <PricePanel>
            <div className="flex flex-col gap-1">
                <span className="text-body text-doberman-black/55">
                    Wags' {benchmarkBreedOf(benchmarkLabel)} price estimate
                </span>
                <span className="text-price text-doberman-black">
                    {benchmarkPrice}
                </span>
            </div>
            <p className="mt-4 text-body text-doberman-black/70">
                No fee listed.
            </p>
        </PricePanel>
    );
}

export function PriceSection({
    data,
    includedInFee,
}: {
    data: ListingData;
    includedInFee?: string[];
}) {
    const fee = data.priceLabel;
    const includesList = formatIncludedList(includedInFee);
    const benchmarkLabel = data.avgLabel;
    const benchmarkPrice = data.avgPrice;

    if (!fee) {
        if (!benchmarkLabel || !benchmarkPrice) return null;
        return (
            <BenchmarkOnlyPrice
                benchmarkPrice={benchmarkPrice}
                benchmarkLabel={benchmarkLabel}
            />
        );
    }

    const showComparison = Boolean(benchmarkLabel && benchmarkPrice);
    const benchmarkBreed = benchmarkLabel && benchmarkBreedOf(benchmarkLabel);
    const showBenchmarkPending = Boolean(benchmarkLabel) && !benchmarkPrice;

    const content = (
        <>
            <div
                className={
                    showComparison ? "grid grid-cols-2" : "flex flex-col"
                }
            >
                <div
                    className={
                        showComparison
                            ? "flex flex-col gap-0.5 pr-4"
                            : "flex flex-col gap-0.5"
                    }
                >
                    <span className="text-price text-doberman-black">
                        {fee}
                    </span>
                    <span className="text-body-small text-doberman-black/55">
                        {data.dogName
                            ? `${data.dogName}'s fee`
                            : "Adoption fee"}
                    </span>
                </div>
                {showComparison && (
                    <div className="flex flex-col gap-0.5 border-l border-doberman-black/10 pl-4">
                        <span className="text-price text-doberman-black/55">
                            {benchmarkPrice}
                        </span>
                        <span className="text-body-small text-doberman-black/55">
                            Wags' {benchmarkBreed} price estimate
                        </span>
                    </div>
                )}
            </div>
            {showBenchmarkPending && (
                <p className="mt-3 text-body-small text-doberman-black/55">
                    We don&rsquo;t have a typical {benchmarkBreed} price to
                    compare yet.
                </p>
            )}
            {includesList && (
                <p className="mt-3 text-body-small text-doberman-black/55">
                    <span className="text-doberman-black/70">
                        Included in the fee:
                    </span>{" "}
                    {includesList}.
                </p>
            )}
        </>
    );

    return <PricePanel>{content}</PricePanel>;
}
